import { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import GarminApiClient from "../../api/GarminApiClient";

export default function GarminAssistantWidget() {
  const garminApi = useMemo(() => new GarminApiClient(), []);

  const [query, setQuery] = useState("");
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [queryResponse, setQueryResponse] = useState<string | null>(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [maxDays, setMaxDays] = useState("30");
  const [sendEmail, setSendEmail] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportResponse, setReportResponse] = useState<string | null>(null);

  const handleQuerySubmit = async () => {
    if (!query.trim()) {
      setQueryError("Please enter a question.");
      return;
    }

    setQueryLoading(true);
    setQueryError(null);
    setQueryResponse(null);

    try {
      const result = await garminApi.query(query.trim());
      if (result.status !== "success") {
        setQueryError(result.error || "Failed to get Garmin response.");
      } else {
        setQueryResponse(result.response);
      }
    } catch (error: any) {
      setQueryError(error.response?.data?.error || "Failed to query Garmin.");
    } finally {
      setQueryLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setReportLoading(true);
    setReportError(null);
    setReportResponse(null);

    const parsedMaxDays = Number(maxDays);
    const request = {
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      max_days: Number.isFinite(parsedMaxDays) ? parsedMaxDays : 30,
      send_email: sendEmail,
    };

    try {
      const result = await garminApi.report(request);
      if (result.status !== "success") {
        setReportError(result.error || "Failed to generate report.");
      } else {
        setReportResponse(result.response);
      }
    } catch (error: any) {
      setReportError(
        error.response?.data?.error || "Failed to generate Garmin report."
      );
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Garmin Assistant
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ask free-form Garmin questions or generate a health report.
      </Typography>

      <TextField
        label="Garmin question"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        multiline
        rows={4}
        fullWidth
        disabled={queryLoading}
        sx={{ mb: 2 }}
      />
      <Button
        variant="contained"
        onClick={handleQuerySubmit}
        disabled={queryLoading || !query.trim()}
      >
        {queryLoading ? <CircularProgress size={24} /> : "Submit Question"}
      </Button>

      {queryError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {queryError}
        </Alert>
      )}

      {queryResponse && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            whiteSpace: "pre-wrap",
          }}
        >
          {queryResponse}
        </Box>
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Generate Report
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Start date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label="End date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label="Max days"
          type="number"
          value={maxDays}
          onChange={(e) => setMaxDays(e.target.value)}
          inputProps={{ min: 1, max: 30 }}
          fullWidth
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
          }
          label="Send report email"
        />
      </Stack>

      <Button
        variant="outlined"
        onClick={handleGenerateReport}
        disabled={reportLoading}
        sx={{ mt: 2 }}
      >
        {reportLoading ? <CircularProgress size={24} /> : "Generate Report"}
      </Button>

      {reportError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {reportError}
        </Alert>
      )}

      {reportResponse && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            whiteSpace: "pre-wrap",
          }}
        >
          {reportResponse}
        </Box>
      )}
    </Paper>
  );
}
